/**
 * WCAG 2.2 colour maths.
 *
 * This module exists because of two findings in `fig/Accessibility-Application.md`:
 *
 *  - X2: the contrast audit is a TOKEN decision. `contrast-gate.py` found 44 failures in the
 *    shipped palette. The corrected palette in `../tokens/palette.ts` is generated against
 *    these functions and proven by the test suite, not eyeballed.
 *  - A1: an agency supplies its own `brandingAccentColor` as a free hex and the Client Portal
 *    repaints from it at runtime. Nothing validated it, so an agency with a pale brand colour
 *    made *their clients'* portal unreadable, on a surface the agency never logs in to.
 *    `safeAccent()` is the fix: never reject their colour, derive a usable one.
 */

export type Rgb = readonly [number, number, number];
export type Rgba = readonly [number, number, number, number];

/** WCAG 2.2 minimum contrast ratios. */
export const WCAG = {
  /** 1.4.3 AA - text below 24px (or below 18.66px bold). */
  text: 4.5,
  /** 1.4.3 AA - text at or above 24px (or 18.66px bold). */
  largeText: 3,
  /** 1.4.11 AA - icons, borders, focus rings, and any graphic carrying meaning. */
  nonText: 3,
  /** 1.4.6 AAA - not our target, exposed so a caller can opt in. */
  textAAA: 7,
} as const;

const clamp = (n: number, lo = 0, hi = 1) => (n < lo ? lo : n > hi ? hi : n);

// ---------------------------------------------------------------- parsing

const HEX = /^#([0-9a-f]{3,8})$/i;
const FUNC = /^rgba?\(([^)]+)\)$/i;

/** Parse `#abc`, `#aabbcc`, `#aabbccdd`, `rgb(...)`, `rgba(...)`. Returns null, never throws. */
export function parseColor(css: string): Rgba | null {
  const v = css.trim();
  const hex = HEX.exec(v);
  if (hex) {
    let h = hex[1]!;
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length === 4) h = h.split('').map((c) => c + c).join('');
    if (h.length === 6) h += 'ff';
    if (h.length !== 8) return null;
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
      parseInt(h.slice(6, 8), 16) / 255,
    ];
  }
  const fn = FUNC.exec(v);
  if (fn) {
    const p = fn[1]!.split(/[,\s/]+/).map((s) => s.trim()).filter(Boolean);
    if (p.length < 3) return null;
    const n = p.slice(0, 4).map(Number);
    if (n.slice(0, 3).some((x) => !Number.isFinite(x))) return null;
    const a = p.length > 3 && Number.isFinite(n[3]!) ? clamp(n[3]!) : 1;
    return [clamp(n[0]!, 0, 255), clamp(n[1]!, 0, 255), clamp(n[2]!, 0, 255), a];
  }
  return null;
}

export function toHex(c: Rgb | Rgba): string {
  const h = (n: number) => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, '0');
  return `#${h(c[0])}${h(c[1])}${h(c[2])}`;
}

// ---------------------------------------------------------------- contrast

const toLinear = (channel: number) => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

/** WCAG 2.x relative luminance. */
export function relativeLuminance(c: Rgb | Rgba): number {
  return 0.2126 * toLinear(c[0]) + 0.7152 * toLinear(c[1]) + 0.0722 * toLinear(c[2]);
}

/**
 * Alpha-composite `fg` over an opaque `bg`.
 *
 * Half a modern palette is rgba(), and judging a translucent token by its nominal colour is
 * meaningless - `rgba(235,240,248,.40)` is not a light colour, it is whatever the thing behind
 * it makes it. Every ratio in this codebase composites first.
 */
export function composite(fg: Rgba, bg: Rgb): Rgb {
  const a = clamp(fg[3]);
  return [
    fg[0] * a + bg[0] * (1 - a),
    fg[1] * a + bg[1] * (1 - a),
    fg[2] * a + bg[2] * (1 - a),
  ];
}

const opaque = (c: Rgb | Rgba, over: Rgb): Rgb =>
  c.length === 4 && (c as Rgba)[3] < 1 ? composite(c as Rgba, over) : [c[0], c[1], c[2]];

/** Contrast ratio, 1..21. `fg` is composited over `bg` when translucent. */
export function contrastRatio(fg: Rgb | Rgba, bg: Rgb | Rgba, base: Rgb = [255, 255, 255]): number {
  const b = opaque(bg, base);
  const f = opaque(fg, b);
  const l1 = relativeLuminance(f);
  const l2 = relativeLuminance(b);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

export function meetsContrast(fg: Rgb | Rgba, bg: Rgb | Rgba, min: number = WCAG.text): boolean {
  return contrastRatio(fg, bg) + 1e-9 >= min;
}

// ---------------------------------------------------------------- HSL, for hue-preserving adjustment

function rgbToHsl(c: Rgb): [number, number, number] {
  const r = c[0] / 255, g = c[1] / 255, b = c[2] / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  if (s === 0) { const v = l * 255; return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const ch = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [ch(h + 1 / 3) * 255, ch(h) * 255, ch(h - 1 / 3) * 255];
}

// ---------------------------------------------------------------- the two fixes

export interface EnsureContrastResult {
  readonly color: Rgb;
  readonly ratio: number;
  /** True when the input already met the target and was returned untouched. */
  readonly unchanged: boolean;
  /** True when even pure black and pure white miss the target against this background. */
  readonly impossible: boolean;
}

/**
 * Return the colour closest to `color` that meets `min` against `bg`, preserving hue.
 *
 * Hue preservation is the point: this runs on a customer's brand colour, and shifting their
 * green to a different green is acceptable where shifting it to grey is not. Lightness is
 * scanned in both directions and whichever qualifying value sits nearest the original wins,
 * so a colour that only just fails moves only just far enough.
 */
export function ensureContrast(color: Rgb, bg: Rgb, min: number = WCAG.nonText): EnsureContrastResult {
  const start = contrastRatio(color, bg);
  if (start + 1e-9 >= min) return { color, ratio: start, unchanged: true, impossible: false };

  const [h, s, l0] = rgbToHsl(color);
  const STEP = 0.002;
  let best: { color: Rgb; ratio: number; distance: number } | null = null;

  for (const dir of [-1, 1] as const) {
    for (let i = 1; i <= Math.ceil(1 / STEP); i++) {
      const l = l0 + dir * i * STEP;
      if (l < 0 || l > 1) break;
      const cand = hslToRgb(h, s, l);
      const ratio = contrastRatio(cand, bg);
      if (ratio + 1e-9 >= min) {
        const distance = Math.abs(l - l0);
        if (!best || distance < best.distance) best = { color: cand, ratio, distance };
        break; // monotonic away from the backdrop - the first hit in this direction is nearest
      }
    }
  }
  if (best) return { color: best.color, ratio: best.ratio, unchanged: false, impossible: false };

  // Saturated hue against a mid-tone backdrop: no lightness of this hue can reach the target.
  // Fall back to the better of black/white rather than returning something that fails.
  const fallback = accessibleOn(bg);
  return { color: fallback, ratio: contrastRatio(fallback, bg), unchanged: false, impossible: true };
}

/** Black or white, whichever reads better on `bg`. The standard on-colour decision. */
export function accessibleOn(bg: Rgb | Rgba, base: Rgb = [255, 255, 255]): Rgb {
  const b = opaque(bg, base);
  const black: Rgb = [0, 0, 0];
  const white: Rgb = [255, 255, 255];
  return contrastRatio(black, b) >= contrastRatio(white, b) ? black : white;
}

export interface SafeAccent {
  /** The agency's colour, untouched. Use for identity only: logo lockup, a rule, a fill. */
  readonly identity: Rgb;
  /** Their colour, adjusted only as far as needed to be legible ON this surface. */
  readonly onSurface: Rgb;
  /** Black or white - what to put ON a fill of `identity`. */
  readonly foreground: Rgb;
  /** Ratio of the raw accent against the surface, before adjustment. */
  readonly rawRatio: number;
  /** True when `onSurface` had to differ from `identity` - the admin should be told. */
  readonly adjusted: boolean;
  /** True when the hue could not reach the target at any lightness. */
  readonly impossible: boolean;
}

/**
 * Finding A1. An agency sets `brandingAccentColor` to their real brand colour; the portal
 * repaints from it. This never rejects their colour - refusing someone's brand is hostile and
 * they will not understand why - it separates the two jobs the colour was doing:
 *
 *   identity  -> their exact colour, as a fill or a mark, with `foreground` on top of it
 *   onSurface -> a legible variant of the same hue, for anything that must be READ
 *
 * `adjusted` is what the admin settings preview should surface: the truth, at the moment they
 * choose it. That is feedback, not friction (checklist N1 counterweight).
 */
export function safeAccent(
  accent: string | Rgb,
  surface: Rgb,
  min: number = WCAG.nonText,
): SafeAccent | null {
  let raw: Rgb;
  if (typeof accent === 'string') {
    const parsed = parseColor(accent);
    if (!parsed) return null;
    raw = composite(parsed, surface);
  } else raw = accent;

  const fixed = ensureContrast(raw, surface, min);
  return {
    identity: raw,
    onSurface: fixed.color,
    foreground: accessibleOn(raw),
    rawRatio: contrastRatio(raw, surface),
    adjusted: !fixed.unchanged,
    impossible: fixed.impossible,
  };
}
